import {
  Alert,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  Button,
} from '@one-thd/sui-atomic-components';
import React, { useEffect, useReducer, useState } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { TDateSelectValues } from '../../../models/common';
import { DateRanges } from '../../../utils/DateRanges';
import {
  getEffectiveFilterDateRange,
  getFilterDetailsFromSession,
} from '../../../utils/purchase-history/common';
import {
  modifyDateFormat,
  parseDateToString,
  setPHFilterToSession,
} from '../../../utils/purchase-history/order-filter-utils';
import { OrderFilterDateSection } from './OrderFilterDateSection';
import { CustomDateDrawer } from './CustomDateDrawer';
import { DateFilterSettings, DateSettings } from '../../../models/order-filter';
import { validateDate } from '../../../utils/purchase-history/date-validation';
import { pushEventInfo } from '../../../utils/analytics';
import { dateTileOptions } from '../../../constants/order-filter';

// --- Reducer types ---

interface DateDrawerState {
  tileSettings: DateSettings;
  customDateRange: DateSettings;
  lastInteraction: 'tile' | 'custom';
}

type DateDrawerAction =
  | {
      type: 'DRAWER_OPENED';
      session?: { selectedDate: string; startDate: string; endDate: string };
    }
  | {
      type: 'TILE_SELECTED';
      date: string;
      startDate: string | null;
      endDate: string | null;
    }
  | {
      type: 'CUSTOM_RANGE_UPDATED';
      update: DateSettings | ((prev: DateSettings) => DateSettings);
    };

// --- Reducer ---

function dateDrawerReducer(
  state: DateDrawerState,
  action: DateDrawerAction,
): DateDrawerState {
  switch (action.type) {
    case 'DRAWER_OPENED': {
      const { session } = action;
      if (
        session?.selectedDate === 'custom' &&
        session.startDate &&
        session.endDate
      ) {
        return {
          ...state,
          customDateRange: {
            selectedDate: 'custom',
            startDate: parseDateToString(session.startDate),
            endDate: parseDateToString(session.endDate),
          },
          tileSettings: { ...state.tileSettings, selectedDate: null },
          lastInteraction: 'custom',
        };
      }
      return {
        ...state,
        customDateRange: {
          selectedDate: 'custom',
          startDate: null,
          endDate: null,
        },
        lastInteraction: 'tile',
      };
    }
    case 'TILE_SELECTED': {
      return {
        tileSettings: {
          ...state.tileSettings,
          selectedDate: action.date as TDateSelectValues,
          startDate: action.startDate,
          endDate: action.endDate,
        },
        customDateRange: {
          selectedDate: 'custom',
          startDate: null,
          endDate: null,
        },
        lastInteraction: 'tile',
      };
    }
    case 'CUSTOM_RANGE_UPDATED': {
      const newCustomRange =
        typeof action.update === 'function'
          ? action.update(state.customDateRange)
          : action.update;
      return {
        ...state,
        customDateRange: newCustomRange,
        tileSettings: { ...state.tileSettings, selectedDate: null },
        lastInteraction: 'custom',
      };
    }
    default:
      return state;
  }
}

// --- Component ---

interface OrderFilterDateDrawerProps {
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrderFilterDateDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
}: OrderFilterDateDrawerProps) => {
  const { dateSelectorValue, pHRequest, setDateSelectorValue, setPHRequest } =
    usePurchaseHistoryContext();

  const [state, dispatch] = useReducer(dateDrawerReducer, undefined, () => {
    const sessionPHFilter = getFilterDetailsFromSession();
    const { startDate, endDate } = getEffectiveFilterDateRange(
      pHRequest,
      sessionPHFilter,
    );
    const toMMDDYYYY = (date: string): string | null => {
      if (!date) return null;
      if (date.includes('/')) return date;
      return parseDateToString(date);
    };
    return {
      tileSettings: {
        selectedDate: sessionPHFilter?.selectedDate ?? dateSelectorValue,
        startDate: toMMDDYYYY(startDate),
        endDate: toMMDDYYYY(endDate),
      },
      customDateRange: {
        selectedDate: 'custom' as const,
        startDate: null,
        endDate: null,
      },
      lastInteraction: 'tile' as const,
    };
  });

  const [filterSettings, setFilterSettings] = useState<DateFilterSettings>({
    customDateUsed: false,
    showStartCalendar: false,
    showEndCalendar: false,
  });

  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (isDrawerOpen) {
      setDateError(null);
      const session = getFilterDetailsFromSession();
      dispatch({ type: 'DRAWER_OPENED', session });
    }
  }, [isDrawerOpen]);

  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onDateSelect = (
    _event: React.SyntheticEvent<Element, Event>,
    date: string,
  ): void => {
    if (date === state.tileSettings.selectedDate) return;
    if (!date) return;

    const startDateStr =
      DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.startDate || '';
    const endDateStr =
      DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.endDate || '';

    dispatch({
      type: 'TILE_SELECTED',
      date,
      startDate: startDateStr ? parseDateToString(startDateStr) : null,
      endDate: endDateStr ? parseDateToString(endDateStr) : null,
    });

    setFilterSettings((prevState) => ({
      ...prevState,
      customDateUsed: false,
    }));
    setDateError(null);
  };

  const handleCustomRangeUpdate: React.Dispatch<
    React.SetStateAction<DateSettings>
  > = (update) => {
    dispatch({ type: 'CUSTOM_RANGE_UPDATED', update });
    setDateError(null);
  };

  const getEffectiveSubmitSettings = () => {
    const { lastInteraction, customDateRange, tileSettings } = state;
    if (lastInteraction === 'custom') {
      return {
        selectedDate: 'custom' as const,
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate,
      };
    }
    return tileSettings;
  };

  const getDateError = (): string | null => {
    const getDates = (): {
      startStr: string | null;
      endStr: string | null;
    } => {
      if (state.lastInteraction === 'custom') {
        return {
          startStr: state.customDateRange.startDate?.toString() ?? null,
          endStr: state.customDateRange.endDate?.toString() ?? null,
        };
      }
      const { tileSettings } = state;
      return {
        startStr: tileSettings.startDate?.toString() ?? null,
        endStr: tileSettings.endDate?.toString() ?? null,
      };
    };

    const { startStr, endStr } = getDates();
    if (!startStr || !endStr) return 'Please enter both a start and end date.';
    if (startStr.length !== 10 || endStr.length !== 10) {
      return 'Please enter a complete date (MM/DD/YYYY).';
    }
    const startError = validateDate(startStr, endStr, 'startDate');
    if (startError) return startError;
    const endError = validateDate(endStr, startStr, 'endDate');
    if (endError) return endError;
    return null;
  };

  const handleDateViewChange = () => {
    const effectiveSettings = getEffectiveSubmitSettings();

    const [newStartDate, newEndDate] = modifyDateFormat(
      effectiveSettings.startDate,
      effectiveSettings.endDate,
    );

    const normalizedSelectedDate: TDateSelectValues =
      (effectiveSettings.selectedDate as TDateSelectValues) ||
      (FeatureSwitches.enableLSSCPS25MonthsDefault
        ? ('last25Months' as TDateSelectValues)
        : ('last6Months' as TDateSelectValues));

    setDateSelectorValue(normalizedSelectedDate);
    setPHRequest((prevState: typeof pHRequest) => {
      const nextStartDate = effectiveSettings.startDate
        ? newStartDate ?? ''
        : '';
      const nextEndDate = effectiveSettings.endDate ? newEndDate ?? '' : '';
      if (
        prevState.startDate === nextStartDate &&
        prevState.endDate === nextEndDate &&
        prevState.pageNumber === 1
      ) {
        return prevState;
      }

      return {
        ...prevState,
        startDate: nextStartDate,
        endDate: nextEndDate,
        pageNumber: 1,
      };
    });
    setPHFilterToSession({
      selectedDate: effectiveSettings.selectedDate,
      startDate: effectiveSettings.startDate ? newStartDate ?? '' : '',
      endDate: effectiveSettings.endDate ? newEndDate ?? '' : '',
    });
    closeFilterDrawer();
    const dateLabel =
      normalizedSelectedDate === 'custom'
        ? 'custom'
        : dateTileOptions.find(
            (option) => option.value === normalizedSelectedDate,
          )?.label ?? normalizedSelectedDate;
    pushEventInfo({
      category: { primaryCategory: 'button submission' },
      eventInfo: { eventName: 'my account settings' },
      myAccount: {
        settings: `purchase history: date placed: ${dateLabel}`,
      },
    });
  };

  return (
    <>
      <Drawer
        open={isDrawerOpen}
        onClose={closeFilterDrawer}
        style={{ zIndex: 20000000010 }}
        data-testid="date-filter-drawer"
      >
        <DrawerHeader title="Date Placed" onClose={closeFilterDrawer} />
        <DrawerBody>
          <OrderFilterDateSection
            tileSelectedDate={state.tileSettings.selectedDate}
            customDateRange={state.customDateRange}
            setCustomDateRange={handleCustomRangeUpdate}
            setFilterSettings={setFilterSettings}
            onDateSelect={onDateSelect}
          />
        </DrawerBody>
        <DrawerFooter>
          {dateError && (
            <div className="sui-mb-2">
              <Alert status="error">{dateError}</Alert>
            </div>
          )}
          <Button
            variant="primary"
            fullWidth
            onClick={() => {
              const error = getDateError();
              if (error) {
                setDateError(error);
                return;
              }
              setDateError(null);
              handleDateViewChange();
            }}
          >
            View Results
          </Button>
        </DrawerFooter>
      </Drawer>
      <CustomDateDrawer
        setFilterSettings={setFilterSettings}
        filterSettings={filterSettings}
        dateSettings={state.customDateRange}
        setDateSettings={handleCustomRangeUpdate}
      />
    </>
  );
};
